export type Language = 'en' | 'fr' | 'ar';

export const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      solutions: 'Solutions',
      pricing: 'Pricing',
      contact: 'Contact',
      signIn: 'Sign in',
      getStarted: 'Get Started Free',
    },
    // Hero
    hero: {
      badge: 'AI-Powered Medical Documentation',
      title: 'Focus on Patients,',
      titleHighlight: 'Not Paperwork',
      description: 'MedKit.ai uses advanced AI to transform your voice into structured medical documentation. Save hours daily while improving patient care.',
      startTrial: 'Start Free Trial',
      watchDemo: 'Book a Demo',
    },
    // Why MedKit (pain-point section)
    whyMedkit: {
      badge: 'The Problem',
      title: 'Documentation Burden Is',
      titleHighlight: 'Burning Out Clinicians',
      description: 'Clinicians spend up to 2 hours daily on paperwork for every hour of patient care. That time should belong to your patients.',
      points: {
        burnout: 'Reduce Documentation Burnout',
        burnoutDesc: 'Clinicians spend more time typing than caring. MedKit.ai gives that time back.',
        errors: 'Fewer Errors, Better Notes',
        errorsDesc: 'AI-structured SOAP notes reduce omissions and improve clinical accuracy.',
        focus: 'Stay Present With Patients',
        focusDesc: 'No more splitting attention between your patient and your keyboard.',
        fast: 'From Voice to Notes in Seconds',
        fastDesc: 'Speak naturally during consultations. Get structured documentation instantly.',
      },
    },
    // Specialties (homepage)
    specialties: {
      badge: 'Built for Your Specialty',
      title: 'Designed for',
      titleHighlight: 'Every Clinician',
      description: 'MedKit.ai adapts to the unique workflow and terminology of your medical specialty.',
    },
    // Compliance (homepage)
    compliance: {
      badge: 'Security & Compliance',
      title: 'Your Patient Data Is',
      titleHighlight: 'Our Top Priority',
      description: 'We are building MedKit.ai with healthcare compliance at its core. Security is not an afterthought.',
      hipaa: 'HIPAA Compliant',
      hipaaDesc: 'Designed to meet HIPAA requirements for protected health information.',
      gdpr: 'GDPR Compliant',
      gdprDesc: 'Full compliance with EU data protection regulations.',
      encryption: 'End-to-End Encryption',
      encryptionDesc: 'All data encrypted in transit and at rest using AES-256.',
      privacy: 'Privacy by Design',
      privacyDesc: 'Your patient data is never used for training. You stay in control.',
    },
    // Features
    features: {
      title: 'Everything You Need to',
      titleHighlight: 'Modernize Your Practice',
      description: 'Powerful AI tools designed specifically for healthcare professionals to streamline documentation and enhance patient care.',
      voice: {
        title: 'Voice-Powered Documentation',
        description: 'Speak naturally during consultations. MedKit.ai transcribes and structures your notes in real-time.',
      },
      ai: {
        title: 'AI Clinical Intelligence',
        description: 'Advanced AI understands medical terminology, suggests diagnoses, and ensures completeness.',
      },
      soap: {
        title: 'Instant SOAP Notes',
        description: 'Automatically generate structured SOAP notes, referral letters, and patient summaries.',
      },
      security: {
        title: 'HIPAA & GDPR Compliant',
        description: 'Enterprise-grade security with end-to-end encryption. Your patient data stays protected.',
      },
    },
    // How it works
    howItWorks: {
      title: 'Three Simple Steps to',
      titleHighlight: 'Better Documentation',
      step1: {
        title: 'Start Recording',
        description: 'Begin your patient consultation as normal. MedKit.ai listens and transcribes in real-time.',
      },
      step2: {
        title: 'AI Processing',
        description: 'Our AI analyzes the conversation, identifies key medical information, and structures the data.',
      },
      step3: {
        title: 'Review & Export',
        description: 'Review the generated SOAP notes, make any edits, and export directly to your EHR.',
      },
    },
    // Testimonials
    testimonials: {
      title: 'Loved by Healthcare',
      titleHighlight: 'Professionals Worldwide',
      quote1: 'MedKit.ai has transformed how I document patient encounters. What used to take 15 minutes now takes 2.',
      quote2: 'The AI suggestions are incredibly accurate. It\'s like having a brilliant scribe who never misses a detail.',
      quote3: 'Finally, I can focus on my patients instead of my keyboard. This is the future of medicine.',
    },
    // CTA
    cta: {
      title: 'Ready to Transform Your Practice?',
      description: 'See how MedKit.ai can save you hours of documentation time every day. Start for free, no credit card required.',
      startTrial: 'Start Free Trial',
      talkToSales: 'Talk to Sales',
      noCreditCard: 'No credit card required • Free trial • Cancel anytime',
    },
    // Footer
    footer: {
      description: 'AI-powered medical documentation assistant. Transforming healthcare workflows with intelligent automation.',
      product: 'Product',
      company: 'Company',
      resources: 'Resources',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      hipaaGdpr: 'HIPAA & GDPR Compliance',
      cookies: 'Cookie Policy',
      madeWith: 'Made with',
      forHealthcare: 'for healthcare',
      rights: 'All rights reserved.',
    },
    // Pricing
    pricing: {
      title: 'Simple, Transparent',
      titleHighlight: 'Pricing',
      description: 'Choose the plan that fits your practice. All plans include a 2-day free trial.',
      monthly: 'Monthly',
      yearly: 'Yearly',
      save: 'Save €2/month',
      perMonth: '/month',
      billedAnnually: 'Billed annually',
      custom: 'Custom',
      startTrial: 'Start Free Trial',
      contactSales: 'Contact Sales',
      compare: 'Compare Plans',
      compareDesc: 'See which plan is right for your needs',
      faq: 'Frequently Asked Questions',
      faqDesc: 'Got questions? We\'ve got answers.',
      stillQuestions: 'Still have questions?',
      stillQuestionsDesc: 'Our team is here to help. Schedule a call with our sales team to find the best solution for your practice.',
      free: 'Free',
      freeWeek: '1 week free trial',
      freemiumDesc: 'Freemium — 1 free week, no credit card',
      startFree: 'Start for Free',
      perUserMonth: '/user/month',
      enterpriseDesc: '10 users included — additional users on request',
      enterpriseNote: '10 users included',
      // Plans
      starter: {
        name: 'Starter',
        description: 'Perfect for individual practitioners',
      },
      professional: {
        name: 'Professional',
        description: 'For busy clinicians who need more',
        badge: 'Most Popular',
      },
      enterprise: {
        name: 'Enterprise',
        description: 'For clinics and healthcare organizations',
      },
      // Features
      voiceDoc: 'Voice documentation',
      aiSoap: 'AI SOAP note generation',
      consultations: 'consultations/month',
      unlimited: 'Unlimited',
      basicTemplates: 'Basic templates',
      allTemplates: 'All templates',
      emailSupport: 'Email support',
      prioritySupport: 'Priority email support',
      ehrIntegration: 'EHR integration',
      imageAnalysis: 'Medical image analysis',
      customTemplates: 'Custom templates',
      analytics: 'Analytics dashboard',
      phoneSupport: '24/7 phone support',
      unlimitedUsers: 'Unlimited users',
      customAi: 'Custom AI training',
      dedicatedManager: 'Dedicated account manager',
      sso: 'SSO / SAML',
      customIntegrations: 'Custom integrations',
      onPremise: 'On-premise deployment',
      sla: 'SLA guarantee',
    },
    // Contact
    contact: {
      title: 'Get in',
      titleHighlight: 'Touch',
      description: 'Have questions about MedKit.ai? We\'re here to help. Reach out and we\'ll get back to you as soon as possible.',
      emailUs: 'Email Us',
      emailDesc: 'Send us an email and we\'ll respond within 24 hours.',
      callUs: 'Call Us',
      callDesc: 'Speak with our team Monday to Friday, 9am-6pm CET.',
      liveChat: 'Live Chat',
      chatDesc: 'Chat with our support team in real-time.',
      startChat: 'Start a conversation',
      sendMessage: 'Send us a message',
      sendMessageDesc: 'Fill out the form below and we\'ll be in touch shortly.',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Work Email',
      company: 'Company/Clinic',
      role: 'Your Role',
      interest: 'I\'m interested in',
      message: 'Message',
      send: 'Send Message',
      sending: 'Sending...',
      sent: 'Message Sent!',
      sentDesc: 'Thank you for reaching out. We\'ll get back to you within 24 hours.',
      sendAnother: 'Send another message',
      interests: {
        demo: 'Scheduling a demo',
        pricing: 'Pricing information',
        enterprise: 'Enterprise solutions',
        integration: 'EHR integration',
        support: 'Technical support',
        other: 'Something else',
      },
      whyContact: 'Why reach out to us?',
      dedicatedTeam: 'Dedicated support team',
      responseTime: '24-hour response time',
      experts: 'Expert product specialists',
      enterpriseSolutions: 'Enterprise solutions available',
      offices: 'Our Headquarters',
      quickDemo: 'Want a quick demo?',
      quickDemoDesc: 'See MedKit.ai in action with a personalized demo from our product team.',
      scheduleDemo: 'Schedule Demo',
      quickAnswers: 'Have more questions?',
      quickAnswersDesc: 'Contact us directly and we\'ll get back to you as soon as possible.',
      viewFaq: 'Contact Us',
      helpCenter: 'Send Email',
    },
    // Solutions
    solutions: {
      badge: 'Comprehensive AI Solutions',
      title: 'Powerful Tools for',
      titleHighlight: 'Modern Healthcare',
      description: 'Discover how MedKit.ai can transform every aspect of your clinical workflow, from documentation to patient communication.',
      tryFree: 'Try It Free',
      viewPricing: 'View Pricing',
      featuresTitle: 'Complete Suite of AI-Powered Features',
      featuresDesc: 'Every tool you need to streamline your practice, all in one platform.',
      specialtiesTitle: 'Built for Every Specialty',
      specialtiesDesc: 'MedKit.ai understands the unique needs of different medical specialties and adapts to your workflow.',
      integrationsTitle: 'Seamless EHR Integration',
      integrationsDesc: 'MedKit.ai integrates with all major EHR systems for a seamless workflow.',
      customIntegration: 'Don\'t see your EHR? Contact us for custom integrations.',
      platformTitle: 'Access Anywhere,',
      platformHighlight: 'On Any Device',
      platformDesc: 'MedKit.ai works wherever you work - in the clinic, at home, or on the go. Access your notes and AI assistant from any device.',
      webApp: 'Web application for desktop and laptop',
      mobileApps: 'Native mobile apps for iOS and Android',
      enterpriseDeployment: 'Enterprise deployment options available',
      securityTitle: 'Security You Can Trust',
      securityDesc: 'We take the security and privacy of patient data seriously. MedKit.ai is built with healthcare compliance at its core.',
      encryption: 'End-to-End Encryption',
      encryptionDesc: 'All data is encrypted in transit and at rest using AES-256 encryption.',
      hipaaGdpr: 'HIPAA & GDPR Compliant',
      hipaaGdprDesc: 'Fully compliant with HIPAA, GDPR, and other healthcare regulations.',
      soc2: 'SOC 2 Type II',
      soc2Desc: 'Certified SOC 2 Type II compliant infrastructure and processes.',
      audits: 'Regular Audits',
      auditsDesc: 'Continuous security monitoring and third-party penetration testing.',
      ctaTitle: 'Ready to See It in Action?',
      ctaDesc: 'Start your free trial today or schedule a personalized demo with our team.',
      // Features list
      voiceDoc: {
        title: 'Voice Documentation',
        description: 'Speak naturally during consultations. Our AI transcribes and structures your notes in real-time, understanding medical terminology with 98% accuracy.',
        benefits: ['Real-time transcription', 'Medical terminology recognition', 'Multi-speaker detection', 'Ambient noise filtering'],
      },
      aiAssistant: {
        title: 'AI Clinical Assistant',
        description: 'Get intelligent suggestions, differential diagnoses, and clinical decision support powered by the latest medical AI models.',
        benefits: ['Differential diagnosis suggestions', 'Drug interaction alerts', 'Clinical guideline references', 'Evidence-based recommendations'],
      },
      soapNotes: {
        title: 'SOAP Note Generation',
        description: 'Automatically generate complete, structured SOAP notes from your consultations. Export directly to your EHR.',
        benefits: ['Customizable templates', 'EHR integration', 'One-click export', 'Batch processing'],
      },
      imageAnalysis: {
        title: 'Medical Image Analysis',
        description: 'Upload X-rays, MRIs, and other medical images for AI-powered analysis and reporting assistance.',
        benefits: ['Multi-modality support', 'Abnormality detection', 'Measurement tools', 'Comparison views'],
      },
      patientComm: {
        title: 'Patient Communication',
        description: 'Generate patient-friendly summaries, aftercare instructions, and referral letters automatically.',
        benefits: ['Plain language summaries', 'Multi-language support', 'Custom letterheads', 'Email integration'],
      },
      analyticsDash: {
        title: 'Analytics Dashboard',
        description: 'Track your practice metrics, documentation time savings, and clinical patterns with powerful analytics.',
        benefits: ['Time tracking', 'Productivity metrics', 'Clinical trends', 'Custom reports'],
      },
      // Specialties
      generalPractice: 'General Practice',
      internalMedicine: 'Internal Medicine',
      pediatrics: 'Pediatrics',
      cardiology: 'Cardiology',
      dermatology: 'Dermatology',
      orthopedics: 'Orthopedics',
      psychiatry: 'Psychiatry',
      radiology: 'Radiology',
    },
  },
  fr: {
    // Navigation
    nav: {
      home: 'Accueil',
      solutions: 'Solutions',
      pricing: 'Tarifs',
      contact: 'Contact',
      signIn: 'Connexion',
      getStarted: 'Essai Gratuit',
    },
    // Hero
    hero: {
      badge: 'Documentation Médicale par IA',
      title: 'Concentrez-vous sur vos Patients,',
      titleHighlight: 'Pas sur la Paperasse',
      description: 'MedKit.ai utilise l\'IA avancée pour transformer votre voix en documentation médicale structurée. Gagnez des heures chaque jour tout en améliorant les soins aux patients.',
      startTrial: 'Essai Gratuit',
      watchDemo: 'Réserver une Démo',
    },
    // Why MedKit (pain-point section)
    whyMedkit: {
      badge: 'Le Problème',
      title: 'La Charge Documentaire',
      titleHighlight: 'Épuise les Cliniciens',
      description: 'Les cliniciens passent jusqu\'à 2 heures par jour sur la paperasse pour chaque heure de soins. Ce temps devrait appartenir à vos patients.',
      points: {
        burnout: 'Réduire l\'Épuisement Documentaire',
        burnoutDesc: 'Les cliniciens passent plus de temps à taper qu\'à soigner. MedKit.ai leur rend ce temps.',
        errors: 'Moins d\'Erreurs, de Meilleures Notes',
        errorsDesc: 'Les notes SOAP structurées par IA réduisent les omissions et améliorent la précision clinique.',
        focus: 'Restez Présent avec vos Patients',
        focusDesc: 'Plus besoin de partager votre attention entre votre patient et votre clavier.',
        fast: 'De la Voix aux Notes en Secondes',
        fastDesc: 'Parlez naturellement pendant les consultations. Obtenez une documentation structurée instantanément.',
      },
    },
    // Specialties (homepage)
    specialties: {
      badge: 'Conçu pour Votre Spécialité',
      title: 'Conçu pour',
      titleHighlight: 'Chaque Clinicien',
      description: 'MedKit.ai s\'adapte au flux de travail et à la terminologie uniques de votre spécialité médicale.',
    },
    // Compliance (homepage)
    compliance: {
      badge: 'Sécurité & Conformité',
      title: 'Vos Données Patients Sont',
      titleHighlight: 'Notre Priorité Absolue',
      description: 'Nous construisons MedKit.ai avec la conformité santé au cœur. La sécurité n\'est pas une réflexion après coup.',
      hipaa: 'Conforme HIPAA',
      hipaaDesc: 'Conçu pour répondre aux exigences HIPAA pour les informations de santé protégées.',
      gdpr: 'Conforme RGPD',
      gdprDesc: 'Conformité totale avec les réglementations européennes de protection des données.',
      encryption: 'Chiffrement de Bout en Bout',
      encryptionDesc: 'Toutes les données chiffrées en transit et au repos avec AES-256.',
      privacy: 'Protection dès la Conception',
      privacyDesc: 'Vos données patients ne sont jamais utilisées pour l\'entraînement. Vous gardez le contrôle.',
    },
    // Features
    features: {
      title: 'Tout ce dont vous avez besoin pour',
      titleHighlight: 'Moderniser Votre Cabinet',
      description: 'Des outils IA puissants conçus spécifiquement pour les professionnels de santé afin de simplifier la documentation et améliorer les soins aux patients.',
      voice: {
        title: 'Documentation Vocale',
        description: 'Parlez naturellement pendant les consultations. MedKit.ai transcrit et structure vos notes en temps réel.',
      },
      ai: {
        title: 'Intelligence Clinique IA',
        description: 'L\'IA avancée comprend la terminologie médicale, suggère des diagnostics et assure la complétude.',
      },
      soap: {
        title: 'Notes SOAP Instantanées',
        description: 'Générez automatiquement des notes SOAP structurées, des lettres de référence et des résumés patients.',
      },
      security: {
        title: 'Conforme HIPAA & RGPD',
        description: 'Sécurité de niveau entreprise avec chiffrement de bout en bout. Vos données patients restent protégées.',
      },
    },
    // How it works
    howItWorks: {
      title: 'Trois Étapes Simples pour une',
      titleHighlight: 'Meilleure Documentation',
      step1: {
        title: 'Commencer l\'Enregistrement',
        description: 'Démarrez votre consultation normalement. MedKit.ai écoute et transcrit en temps réel.',
      },
      step2: {
        title: 'Traitement IA',
        description: 'Notre IA analyse la conversation, identifie les informations médicales clés et structure les données.',
      },
      step3: {
        title: 'Réviser & Exporter',
        description: 'Révisez les notes SOAP générées, effectuez les modifications et exportez directement vers votre DSE.',
      },
    },
    // Testimonials
    testimonials: {
      title: 'Apprécié par les Professionnels',
      titleHighlight: 'de Santé du Monde Entier',
      quote1: 'MedKit.ai a transformé ma façon de documenter les consultations. Ce qui prenait 15 minutes ne prend plus que 2.',
      quote2: 'Les suggestions de l\'IA sont incroyablement précises. C\'est comme avoir un scribe brillant qui ne rate jamais un détail.',
      quote3: 'Enfin, je peux me concentrer sur mes patients plutôt que sur mon clavier. C\'est l\'avenir de la médecine.',
    },
    // CTA
    cta: {
      title: 'Prêt à Transformer Votre Cabinet ?',
      description: 'Découvrez comment MedKit.ai peut vous faire gagner des heures de documentation chaque jour. Commencez gratuitement, sans carte bancaire.',
      startTrial: 'Essai Gratuit',
      talkToSales: 'Parler aux Ventes',
      noCreditCard: 'Sans carte bancaire • Essai gratuit • Annulation à tout moment',
    },
    // Footer
    footer: {
      description: 'Assistant de documentation médicale alimenté par l\'IA. Transformation des flux de travail de santé avec l\'automatisation intelligente.',
      product: 'Produit',
      company: 'Entreprise',
      resources: 'Ressources',
      legal: 'Légal',
      privacy: 'Politique de Confidentialité',
      terms: 'Conditions d\'Utilisation',
      hipaaGdpr: 'Conformité HIPAA & RGPD',
      cookies: 'Politique des Cookies',
      madeWith: 'Fait avec',
      forHealthcare: 'pour la santé',
      rights: 'Tous droits réservés.',
    },
    // Pricing
    pricing: {
      title: 'Tarification Simple et',
      titleHighlight: 'Transparente',
      description: 'Choisissez le forfait qui correspond à votre cabinet. Tous les forfaits incluent un essai gratuit de 2 jours.',
      monthly: 'Mensuel',
      yearly: 'Annuel',
      save: 'Économisez 2€/mois',
      perMonth: '/mois',
      billedAnnually: 'Facturé annuellement',
      custom: 'Sur mesure',
      startTrial: 'Essai Gratuit',
      contactSales: 'Contacter les Ventes',
      compare: 'Comparer les Forfaits',
      compareDesc: 'Trouvez le forfait adapté à vos besoins',
      faq: 'Questions Fréquentes',
      faqDesc: 'Des questions ? Nous avons les réponses.',
      stillQuestions: 'Encore des questions ?',
      stillQuestionsDesc: 'Notre équipe est là pour vous aider. Planifiez un appel avec notre équipe commerciale pour trouver la meilleure solution pour votre cabinet.',
      free: 'Gratuit',
      freeWeek: '1 semaine gratuite',
      freemiumDesc: 'Freemium — 1 semaine gratuite, sans carte bancaire',
      startFree: 'Commencer gratuitement',
      perUserMonth: '/utilisateur/mois',
      enterpriseDesc: '10 utilisateurs inclus — utilisateurs supplémentaires sur devis',
      enterpriseNote: '10 utilisateurs inclus',
      starter: {
        name: 'Starter',
        description: 'Parfait pour les praticiens individuels',
      },
      professional: {
        name: 'Professionnel',
        description: 'Pour les cliniciens qui ont besoin de plus',
        badge: 'Le Plus Populaire',
      },
      enterprise: {
        name: 'Entreprise',
        description: 'Pour les cliniques et organisations de santé',
      },
      voiceDoc: 'Documentation vocale',
      aiSoap: 'Génération de notes SOAP par IA',
      consultations: 'consultations/mois',
      unlimited: 'Illimité',
      basicTemplates: 'Modèles de base',
      allTemplates: 'Tous les modèles',
      emailSupport: 'Support par email',
      prioritySupport: 'Support email prioritaire',
      ehrIntegration: 'Intégration DSE',
      imageAnalysis: 'Analyse d\'images médicales',
      customTemplates: 'Modèles personnalisés',
      analytics: 'Tableau de bord analytique',
      phoneSupport: 'Support téléphonique 24/7',
      unlimitedUsers: 'Utilisateurs illimités',
      customAi: 'Formation IA personnalisée',
      dedicatedManager: 'Gestionnaire de compte dédié',
      sso: 'SSO / SAML',
      customIntegrations: 'Intégrations personnalisées',
      onPremise: 'Déploiement sur site',
      sla: 'Garantie SLA',
    },
    // Contact
    contact: {
      title: 'Contactez-',
      titleHighlight: 'Nous',
      description: 'Des questions sur MedKit.ai ? Nous sommes là pour vous aider. Contactez-nous et nous vous répondrons dans les plus brefs délais.',
      emailUs: 'Envoyez un Email',
      emailDesc: 'Envoyez-nous un email et nous répondrons sous 24 heures.',
      callUs: 'Appelez-Nous',
      callDesc: 'Parlez à notre équipe du lundi au vendredi, 9h-18h CET.',
      liveChat: 'Chat en Direct',
      chatDesc: 'Discutez avec notre équipe de support en temps réel.',
      startChat: 'Démarrer une conversation',
      sendMessage: 'Envoyez-nous un message',
      sendMessageDesc: 'Remplissez le formulaire ci-dessous et nous vous contacterons rapidement.',
      firstName: 'Prénom',
      lastName: 'Nom',
      email: 'Email Professionnel',
      company: 'Entreprise/Clinique',
      role: 'Votre Fonction',
      interest: 'Je suis intéressé par',
      message: 'Message',
      send: 'Envoyer le Message',
      sending: 'Envoi en cours...',
      sent: 'Message Envoyé !',
      sentDesc: 'Merci de nous avoir contacté. Nous vous répondrons sous 24 heures.',
      sendAnother: 'Envoyer un autre message',
      interests: {
        demo: 'Planifier une démo',
        pricing: 'Informations tarifaires',
        enterprise: 'Solutions entreprise',
        integration: 'Intégration DSE',
        support: 'Support technique',
        other: 'Autre chose',
      },
      whyContact: 'Pourquoi nous contacter ?',
      dedicatedTeam: 'Équipe de support dédiée',
      responseTime: 'Réponse sous 24 heures',
      experts: 'Spécialistes produit experts',
      enterpriseSolutions: 'Solutions entreprise disponibles',
      offices: 'Notre Siège',
      quickDemo: 'Vous voulez une démo rapide ?',
      quickDemoDesc: 'Découvrez MedKit.ai en action avec une démo personnalisée de notre équipe produit.',
      scheduleDemo: 'Planifier une Démo',
      quickAnswers: 'Vous avez d\'autres questions ?',
      quickAnswersDesc: 'Contactez-nous directement et nous vous répondrons dans les plus brefs délais.',
      viewFaq: 'Nous Contacter',
      helpCenter: 'Envoyer un Email',
    },
    // Solutions
    solutions: {
      badge: 'Solutions IA Complètes',
      title: 'Des Outils Puissants pour la',
      titleHighlight: 'Santé Moderne',
      description: 'Découvrez comment MedKit.ai peut transformer chaque aspect de votre flux de travail clinique, de la documentation à la communication patient.',
      tryFree: 'Essayer Gratuitement',
      viewPricing: 'Voir les Tarifs',
      featuresTitle: 'Suite Complète de Fonctionnalités IA',
      featuresDesc: 'Tous les outils dont vous avez besoin pour optimiser votre cabinet, sur une seule plateforme.',
      specialtiesTitle: 'Conçu pour Chaque Spécialité',
      specialtiesDesc: 'MedKit.ai comprend les besoins uniques des différentes spécialités médicales et s\'adapte à votre flux de travail.',
      integrationsTitle: 'Intégration DSE Transparente',
      integrationsDesc: 'MedKit.ai s\'intègre à tous les principaux systèmes DSE pour un flux de travail fluide.',
      customIntegration: 'Vous ne voyez pas votre DSE ? Contactez-nous pour des intégrations personnalisées.',
      platformTitle: 'Accessible Partout,',
      platformHighlight: 'Sur Tout Appareil',
      platformDesc: 'MedKit.ai fonctionne où que vous soyez - au cabinet, à la maison ou en déplacement. Accédez à vos notes et à l\'assistant IA depuis n\'importe quel appareil.',
      webApp: 'Application web pour ordinateur et portable',
      mobileApps: 'Applications mobiles natives pour iOS et Android',
      enterpriseDeployment: 'Options de déploiement entreprise disponibles',
      securityTitle: 'Une Sécurité de Confiance',
      securityDesc: 'Nous prenons la sécurité et la confidentialité des données patients au sérieux. MedKit.ai est conçu avec la conformité santé au cœur.',
      encryption: 'Chiffrement de Bout en Bout',
      encryptionDesc: 'Toutes les données sont chiffrées en transit et au repos avec le chiffrement AES-256.',
      hipaaGdpr: 'Conforme HIPAA & RGPD',
      hipaaGdprDesc: 'Entièrement conforme à HIPAA, RGPD et autres réglementations de santé.',
      soc2: 'SOC 2 Type II',
      soc2Desc: 'Infrastructure et processus certifiés SOC 2 Type II.',
      audits: 'Audits Réguliers',
      auditsDesc: 'Surveillance de sécurité continue et tests de pénétration tiers.',
      ctaTitle: 'Prêt à le Voir en Action ?',
      ctaDesc: 'Commencez votre essai gratuit aujourd\'hui ou planifiez une démo personnalisée avec notre équipe.',
      voiceDoc: {
        title: 'Documentation Vocale',
        description: 'Parlez naturellement pendant les consultations. Notre IA transcrit et structure vos notes en temps réel, comprenant la terminologie médicale avec 98% de précision.',
        benefits: ['Transcription en temps réel', 'Reconnaissance de terminologie médicale', 'Détection multi-locuteurs', 'Filtrage du bruit ambiant'],
      },
      aiAssistant: {
        title: 'Assistant Clinique IA',
        description: 'Obtenez des suggestions intelligentes, des diagnostics différentiels et un support de décision clinique alimenté par les derniers modèles d\'IA médicale.',
        benefits: ['Suggestions de diagnostic différentiel', 'Alertes d\'interactions médicamenteuses', 'Références aux guidelines cliniques', 'Recommandations basées sur les preuves'],
      },
      soapNotes: {
        title: 'Génération de Notes SOAP',
        description: 'Générez automatiquement des notes SOAP complètes et structurées à partir de vos consultations. Exportez directement vers votre DSE.',
        benefits: ['Modèles personnalisables', 'Intégration DSE', 'Export en un clic', 'Traitement par lots'],
      },
      imageAnalysis: {
        title: 'Analyse d\'Images Médicales',
        description: 'Téléchargez des radiographies, IRM et autres images médicales pour une analyse et une assistance au rapport alimentées par l\'IA.',
        benefits: ['Support multi-modalités', 'Détection d\'anomalies', 'Outils de mesure', 'Vues comparatives'],
      },
      patientComm: {
        title: 'Communication Patient',
        description: 'Générez des résumés compréhensibles pour les patients, des instructions de suivi et des lettres de référence automatiquement.',
        benefits: ['Résumés en langage simple', 'Support multilingue', 'En-têtes personnalisés', 'Intégration email'],
      },
      analyticsDash: {
        title: 'Tableau de Bord Analytique',
        description: 'Suivez vos métriques de cabinet, les gains de temps de documentation et les patterns cliniques avec des analyses puissantes.',
        benefits: ['Suivi du temps', 'Métriques de productivité', 'Tendances cliniques', 'Rapports personnalisés'],
      },
      generalPractice: 'Médecine Générale',
      internalMedicine: 'Médecine Interne',
      pediatrics: 'Pédiatrie',
      cardiology: 'Cardiologie',
      dermatology: 'Dermatologie',
      orthopedics: 'Orthopédie',
      psychiatry: 'Psychiatrie',
      radiology: 'Radiologie',
    },
  },
  ar: {
    // Navigation
    nav: {
      home: 'الرئيسية',
      solutions: 'الحلول',
      pricing: 'الأسعار',
      contact: 'اتصل بنا',
      signIn: 'تسجيل الدخول',
      getStarted: 'ابدأ مجاناً',
    },
    // Hero
    hero: {
      badge: 'توثيق طبي مدعوم بالذكاء الاصطناعي',
      title: 'ركز على المرضى،',
      titleHighlight: 'ليس على الأوراق',
      description: 'يستخدم MedKit.ai الذكاء الاصطناعي المتقدم لتحويل صوتك إلى توثيق طبي منظم. وفر ساعات يومياً مع تحسين رعاية المرضى.',
      startTrial: 'ابدأ تجربة مجانية',
      watchDemo: 'احجز عرضاً توضيحياً',
    },
    // Why MedKit (pain-point section)
    whyMedkit: {
      badge: 'المشكلة',
      title: 'عبء التوثيق',
      titleHighlight: 'يُرهق الأطباء',
      description: 'يقضي الأطباء ما يصل إلى ساعتين يومياً في الأوراق مقابل كل ساعة رعاية. هذا الوقت يجب أن يكون لمرضاك.',
      points: {
        burnout: 'تقليل إرهاق التوثيق',
        burnoutDesc: 'الأطباء يقضون وقتاً في الكتابة أكثر من الرعاية. MedKit.ai يعيد لهم هذا الوقت.',
        errors: 'أخطاء أقل، ملاحظات أفضل',
        errorsDesc: 'ملاحظات SOAP المنظمة بالذكاء الاصطناعي تقلل السهو وتحسن الدقة السريرية.',
        focus: 'ابقَ حاضراً مع مرضاك',
        focusDesc: 'لا حاجة لتقسيم انتباهك بين مريضك ولوحة المفاتيح.',
        fast: 'من الصوت إلى الملاحظات في ثوانٍ',
        fastDesc: 'تحدث بشكل طبيعي أثناء الاستشارات. احصل على توثيق منظم فوراً.',
      },
    },
    // Specialties (homepage)
    specialties: {
      badge: 'مصمم لتخصصك',
      title: 'مصمم لكل',
      titleHighlight: 'طبيب',
      description: 'يتكيف MedKit.ai مع سير العمل والمصطلحات الفريدة لتخصصك الطبي.',
    },
    // Compliance (homepage)
    compliance: {
      badge: 'الأمان والامتثال',
      title: 'بيانات مرضاك هي',
      titleHighlight: 'أولويتنا القصوى',
      description: 'نبني MedKit.ai مع الامتثال للرعاية الصحية في جوهره. الأمان ليس فكرة لاحقة.',
      hipaa: 'متوافق مع HIPAA',
      hipaaDesc: 'مصمم لتلبية متطلبات HIPAA لحماية المعلومات الصحية.',
      gdpr: 'متوافق مع GDPR',
      gdprDesc: 'امتثال كامل مع لوائح حماية البيانات الأوروبية.',
      encryption: 'تشفير من طرف إلى طرف',
      encryptionDesc: 'جميع البيانات مشفرة أثناء النقل والتخزين باستخدام AES-256.',
      privacy: 'الخصوصية حسب التصميم',
      privacyDesc: 'بيانات مرضاك لا تُستخدم أبداً للتدريب. أنت تبقى مسيطراً.',
    },
    // Features
    features: {
      title: 'كل ما تحتاجه',
      titleHighlight: 'لتحديث عيادتك',
      description: 'أدوات ذكاء اصطناعي قوية مصممة خصيصاً للمتخصصين في الرعاية الصحية لتبسيط التوثيق وتحسين رعاية المرضى.',
      voice: {
        title: 'التوثيق الصوتي',
        description: 'تحدث بشكل طبيعي أثناء الاستشارات. يقوم MedKit.ai بنسخ وتنظيم ملاحظاتك في الوقت الفعلي.',
      },
      ai: {
        title: 'الذكاء السريري',
        description: 'الذكاء الاصطناعي المتقدم يفهم المصطلحات الطبية، ويقترح التشخيصات، ويضمن الاكتمال.',
      },
      soap: {
        title: 'ملاحظات SOAP فورية',
        description: 'إنشاء ملاحظات SOAP منظمة تلقائياً، ورسائل الإحالة، وملخصات المرضى.',
      },
      security: {
        title: 'متوافق مع HIPAA و GDPR',
        description: 'أمان على مستوى المؤسسات مع تشفير من طرف إلى طرف. بيانات المرضى تبقى محمية.',
      },
    },
    // How it works
    howItWorks: {
      title: 'ثلاث خطوات بسيطة نحو',
      titleHighlight: 'توثيق أفضل',
      step1: {
        title: 'ابدأ التسجيل',
        description: 'ابدأ استشارة المريض كالمعتاد. يستمع MedKit.ai وينسخ في الوقت الفعلي.',
      },
      step2: {
        title: 'معالجة الذكاء الاصطناعي',
        description: 'يحلل الذكاء الاصطناعي المحادثة، ويحدد المعلومات الطبية الرئيسية، وينظم البيانات.',
      },
      step3: {
        title: 'المراجعة والتصدير',
        description: 'راجع ملاحظات SOAP المُنشأة، وأجرِ أي تعديلات، وصدّر مباشرة إلى نظام السجلات الصحية.',
      },
    },
    // Testimonials
    testimonials: {
      title: 'محبوب من المتخصصين',
      titleHighlight: 'في الرعاية الصحية حول العالم',
      quote1: 'لقد غيّر MedKit.ai طريقة توثيقي لزيارات المرضى. ما كان يستغرق 15 دقيقة أصبح يستغرق 2 فقط.',
      quote2: 'اقتراحات الذكاء الاصطناعي دقيقة بشكل لا يصدق. إنه مثل وجود كاتب عبقري لا يفوت أي تفصيل.',
      quote3: 'أخيراً، يمكنني التركيز على مرضاي بدلاً من لوحة المفاتيح. هذا هو مستقبل الطب.',
    },
    // CTA
    cta: {
      title: 'هل أنت مستعد لتحويل عيادتك؟',
      description: 'اكتشف كيف يمكن لـ MedKit.ai توفير ساعات من التوثيق كل يوم. ابدأ مجاناً، بدون بطاقة ائتمان.',
      startTrial: 'ابدأ تجربة مجانية',
      talkToSales: 'تحدث إلى المبيعات',
      noCreditCard: 'لا حاجة لبطاقة ائتمان • تجربة مجانية • إلغاء في أي وقت',
    },
    // Footer
    footer: {
      description: 'مساعد توثيق طبي مدعوم بالذكاء الاصطناعي. تحويل سير العمل في الرعاية الصحية مع الأتمتة الذكية.',
      product: 'المنتج',
      company: 'الشركة',
      resources: 'الموارد',
      legal: 'قانوني',
      privacy: 'سياسة الخصوصية',
      terms: 'شروط الخدمة',
      hipaaGdpr: 'التوافق مع HIPAA و GDPR',
      cookies: 'سياسة ملفات تعريف الارتباط',
      madeWith: 'صنع بـ',
      forHealthcare: 'للرعاية الصحية',
      rights: 'جميع الحقوق محفوظة.',
    },
    // Pricing
    pricing: {
      title: 'أسعار بسيطة',
      titleHighlight: 'وشفافة',
      description: 'اختر الخطة التي تناسب عيادتك. جميع الخطط تتضمن تجربة مجانية لمدة يومين.',
      monthly: 'شهري',
      yearly: 'سنوي',
      save: 'وفر 2€/شهر',
      perMonth: '/شهر',
      billedAnnually: 'يُفوتر سنوياً',
      custom: 'مخصص',
      startTrial: 'ابدأ تجربة مجانية',
      contactSales: 'اتصل بالمبيعات',
      compare: 'قارن الخطط',
      compareDesc: 'اعرف الخطة المناسبة لاحتياجاتك',
      faq: 'الأسئلة الشائعة',
      faqDesc: 'لديك أسئلة؟ لدينا إجابات.',
      stillQuestions: 'لا تزال لديك أسئلة؟',
      stillQuestionsDesc: 'فريقنا هنا للمساعدة. حدد موعد مكالمة مع فريق المبيعات لإيجاد أفضل حل لعيادتك.',
      free: 'مجاني',
      freeWeek: 'أسبوع مجاني',
      freemiumDesc: 'مجاني — أسبوع مجاني، بدون بطاقة ائتمان',
      startFree: 'ابدأ مجاناً',
      perUserMonth: '/مستخدم/شهر',
      enterpriseDesc: '10 مستخدمين مشمولين — مستخدمون إضافيون عند الطلب',
      enterpriseNote: '10 مستخدمين مشمولين',
      starter: {
        name: 'المبتدئ',
        description: 'مثالي للممارسين الفرديين',
      },
      professional: {
        name: 'المحترف',
        description: 'للأطباء الذين يحتاجون المزيد',
        badge: 'الأكثر شعبية',
      },
      enterprise: {
        name: 'المؤسسات',
        description: 'للعيادات ومنظمات الرعاية الصحية',
      },
      voiceDoc: 'التوثيق الصوتي',
      aiSoap: 'إنشاء ملاحظات SOAP بالذكاء الاصطناعي',
      consultations: 'استشارة/شهر',
      unlimited: 'غير محدود',
      basicTemplates: 'قوالب أساسية',
      allTemplates: 'جميع القوالب',
      emailSupport: 'دعم بالبريد الإلكتروني',
      prioritySupport: 'دعم بريد إلكتروني أولوي',
      ehrIntegration: 'تكامل السجلات الصحية',
      imageAnalysis: 'تحليل الصور الطبية',
      customTemplates: 'قوالب مخصصة',
      analytics: 'لوحة التحليلات',
      phoneSupport: 'دعم هاتفي 24/7',
      unlimitedUsers: 'مستخدمين غير محدودين',
      customAi: 'تدريب ذكاء اصطناعي مخصص',
      dedicatedManager: 'مدير حساب مخصص',
      sso: 'SSO / SAML',
      customIntegrations: 'تكاملات مخصصة',
      onPremise: 'نشر في الموقع',
      sla: 'ضمان SLA',
    },
    // Contact
    contact: {
      title: 'تواصل',
      titleHighlight: 'معنا',
      description: 'لديك أسئلة حول MedKit.ai؟ نحن هنا للمساعدة. تواصل معنا وسنرد عليك في أقرب وقت ممكن.',
      emailUs: 'راسلنا',
      emailDesc: 'أرسل لنا بريداً إلكترونياً وسنرد خلال 24 ساعة.',
      callUs: 'اتصل بنا',
      callDesc: 'تحدث مع فريقنا من الإثنين إلى الجمعة، 9 صباحاً - 6 مساءً بتوقيت وسط أوروبا.',
      liveChat: 'الدردشة المباشرة',
      chatDesc: 'تحدث مع فريق الدعم في الوقت الفعلي.',
      startChat: 'ابدأ محادثة',
      sendMessage: 'أرسل لنا رسالة',
      sendMessageDesc: 'املأ النموذج أدناه وسنتواصل معك قريباً.',
      firstName: 'الاسم الأول',
      lastName: 'اسم العائلة',
      email: 'البريد الإلكتروني المهني',
      company: 'الشركة/العيادة',
      role: 'منصبك',
      interest: 'أنا مهتم بـ',
      message: 'الرسالة',
      send: 'إرسال الرسالة',
      sending: 'جارٍ الإرسال...',
      sent: 'تم إرسال الرسالة!',
      sentDesc: 'شكراً لتواصلك معنا. سنرد عليك خلال 24 ساعة.',
      sendAnother: 'إرسال رسالة أخرى',
      interests: {
        demo: 'جدولة عرض توضيحي',
        pricing: 'معلومات الأسعار',
        enterprise: 'حلول المؤسسات',
        integration: 'تكامل السجلات الصحية',
        support: 'الدعم الفني',
        other: 'شيء آخر',
      },
      whyContact: 'لماذا تتواصل معنا؟',
      dedicatedTeam: 'فريق دعم مخصص',
      responseTime: 'وقت استجابة 24 ساعة',
      experts: 'متخصصون خبراء في المنتج',
      enterpriseSolutions: 'حلول مؤسسية متاحة',
      offices: 'مقرنا الرئيسي',
      quickDemo: 'تريد عرضاً سريعاً؟',
      quickDemoDesc: 'شاهد MedKit.ai قيد التشغيل مع عرض مخصص من فريق المنتج.',
      scheduleDemo: 'جدولة عرض',
      quickAnswers: 'لديك المزيد من الأسئلة؟',
      quickAnswersDesc: 'تواصل معنا مباشرة وسنرد عليك في أقرب وقت ممكن.',
      viewFaq: 'اتصل بنا',
      helpCenter: 'أرسل بريداً إلكترونياً',
    },
    // Solutions
    solutions: {
      badge: 'حلول ذكاء اصطناعي شاملة',
      title: 'أدوات قوية للرعاية',
      titleHighlight: 'الصحية الحديثة',
      description: 'اكتشف كيف يمكن لـ MedKit.ai تحويل كل جانب من جوانب سير عملك السريري، من التوثيق إلى التواصل مع المرضى.',
      tryFree: 'جرب مجاناً',
      viewPricing: 'عرض الأسعار',
      featuresTitle: 'مجموعة كاملة من الميزات المدعومة بالذكاء الاصطناعي',
      featuresDesc: 'جميع الأدوات التي تحتاجها لتحسين عيادتك، كلها في منصة واحدة.',
      specialtiesTitle: 'مصمم لكل تخصص',
      specialtiesDesc: 'يفهم MedKit.ai الاحتياجات الفريدة للتخصصات الطبية المختلفة ويتكيف مع سير عملك.',
      integrationsTitle: 'تكامل سلس مع السجلات الصحية',
      integrationsDesc: 'يتكامل MedKit.ai مع جميع أنظمة السجلات الصحية الرئيسية لسير عمل سلس.',
      customIntegration: 'لا ترى نظام السجلات الخاص بك؟ اتصل بنا للتكاملات المخصصة.',
      platformTitle: 'الوصول من أي مكان،',
      platformHighlight: 'على أي جهاز',
      platformDesc: 'يعمل MedKit.ai أينما كنت - في العيادة، في المنزل، أو أثناء التنقل. الوصول إلى ملاحظاتك ومساعد الذكاء الاصطناعي من أي جهاز.',
      webApp: 'تطبيق ويب لسطح المكتب والكمبيوتر المحمول',
      mobileApps: 'تطبيقات جوال أصلية لـ iOS و Android',
      enterpriseDeployment: 'خيارات نشر مؤسسية متاحة',
      securityTitle: 'أمان يمكنك الوثوق به',
      securityDesc: 'نأخذ أمان وخصوصية بيانات المرضى على محمل الجد. تم بناء MedKit.ai مع الامتثال للرعاية الصحية في جوهره.',
      encryption: 'تشفير من طرف إلى طرف',
      encryptionDesc: 'جميع البيانات مشفرة أثناء النقل والتخزين باستخدام تشفير AES-256.',
      hipaaGdpr: 'متوافق مع HIPAA و GDPR',
      hipaaGdprDesc: 'متوافق بالكامل مع HIPAA و GDPR ولوائح الرعاية الصحية الأخرى.',
      soc2: 'SOC 2 Type II',
      soc2Desc: 'بنية تحتية وعمليات معتمدة من SOC 2 Type II.',
      audits: 'تدقيقات منتظمة',
      auditsDesc: 'مراقبة أمنية مستمرة واختبار اختراق من طرف ثالث.',
      ctaTitle: 'هل أنت مستعد لرؤيته قيد التشغيل؟',
      ctaDesc: 'ابدأ تجربتك المجانية اليوم أو حدد موعد عرض مخصص مع فريقنا.',
      voiceDoc: {
        title: 'التوثيق الصوتي',
        description: 'تحدث بشكل طبيعي أثناء الاستشارات. يقوم الذكاء الاصطناعي بنسخ وتنظيم ملاحظاتك في الوقت الفعلي، مع فهم المصطلحات الطبية بدقة 98%.',
        benefits: ['نسخ في الوقت الفعلي', 'التعرف على المصطلحات الطبية', 'كشف المتحدثين المتعددين', 'تصفية الضوضاء المحيطة'],
      },
      aiAssistant: {
        title: 'المساعد السريري بالذكاء الاصطناعي',
        description: 'احصل على اقتراحات ذكية، تشخيصات تفاضلية، ودعم القرار السريري مدعوم بأحدث نماذج الذكاء الاصطناعي الطبي.',
        benefits: ['اقتراحات التشخيص التفاضلي', 'تنبيهات تفاعلات الأدوية', 'مراجع الإرشادات السريرية', 'توصيات مبنية على الأدلة'],
      },
      soapNotes: {
        title: 'إنشاء ملاحظات SOAP',
        description: 'إنشاء ملاحظات SOAP كاملة ومنظمة تلقائياً من استشاراتك. التصدير مباشرة إلى نظام السجلات الصحية.',
        benefits: ['قوالب قابلة للتخصيص', 'تكامل السجلات الصحية', 'تصدير بنقرة واحدة', 'معالجة دفعية'],
      },
      imageAnalysis: {
        title: 'تحليل الصور الطبية',
        description: 'قم بتحميل الأشعة السينية والرنين المغناطيسي وغيرها من الصور الطبية للتحليل بالذكاء الاصطناعي والمساعدة في التقارير.',
        benefits: ['دعم متعدد الأنماط', 'كشف الشذوذات', 'أدوات القياس', 'عروض المقارنة'],
      },
      patientComm: {
        title: 'التواصل مع المريض',
        description: 'إنشاء ملخصات سهلة الفهم للمرضى، تعليمات الرعاية اللاحقة، ورسائل الإحالة تلقائياً.',
        benefits: ['ملخصات بلغة بسيطة', 'دعم متعدد اللغات', 'ترويسات مخصصة', 'تكامل البريد الإلكتروني'],
      },
      analyticsDash: {
        title: 'لوحة التحليلات',
        description: 'تتبع مقاييس عيادتك، توفير وقت التوثيق، والأنماط السريرية مع تحليلات قوية.',
        benefits: ['تتبع الوقت', 'مقاييس الإنتاجية', 'الاتجاهات السريرية', 'تقارير مخصصة'],
      },
      generalPractice: 'الطب العام',
      internalMedicine: 'الطب الباطني',
      pediatrics: 'طب الأطفال',
      cardiology: 'أمراض القلب',
      dermatology: 'الأمراض الجلدية',
      orthopedics: 'جراحة العظام',
      psychiatry: 'الطب النفسي',
      radiology: 'الأشعة',
    },
  },
};

export const getDirection = (lang: Language): 'ltr' | 'rtl' => {
  return lang === 'ar' ? 'rtl' : 'ltr';
};
